/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
*/

/**
 * @module bulb-progress/bulb-transcoding/bulbtranscodingprogress
 */

import {
  DowncastConversionApi, ViewDowncastWriter, LocaleTranslate, Plugin, ViewElement,
} from 'ckeditor5';
import type {
  Editor, ModelPosition, ModelElement, ViewContainerElement,
} from 'ckeditor5';
import uploadingPlaceholder from '../theme/icons/image_placeholder.svg';
import {
  checkTranscodingProgress,
  getUpdatedContentBlock,
  generateTranscodingBackground,
} from './bulbtranscodingutils';
import { toTitleCase } from '../../utils';
import {
  clean,
  getUIElement,
  initializeProgressBar,
  refreshAsset,
  startAppearEffect,
  updateProgressBar,
  refreshVideo,
} from '../bulbprogressutils';

/**
 * The image upload progress plugin.
 * It shows a placeholder when the image is read from the disk and a progress bar while the image is uploading.
 *
 * @extends Plugin
 */
export default class BulbTranscodingProgress extends Plugin {

  placeholder: string;

  /**
   * @inheritDoc
   */
  constructor(editor: Editor) {
    super(editor);

    /**
     * The image placeholder that is displayed before real image data can be accessed.
     *
     * @protected
     * @member {String} #placeholder
     */
    this.placeholder = `data:image/svg+xml;utf8,${encodeURIComponent(uploadingPlaceholder)}`;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbTranscodingProgress' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    // TODO: Create our own event listener here that still has access to the downcast conversionApi.
    editor.editing.downcastDispatcher.on(
      'insert:bulbTranscoder',
      (evt, data, conversionApi) => {
        this.transcodingStatusChange(data, conversionApi);
      },
    );
  }

  /**
   * Method that gets invoked upon the creation and insertion of our custom upload placeholder.
   *
   * @param data : Object= > additional information about the change.
   * @param conversionApi Conversion interface to be used by callback, passed in DowncastDispatcher constructor.
   */
  private transcodingStatusChange(data: { item: ModelElement }, conversionApi: DowncastConversionApi) {
    const { editor } = this;
    const { model } = editor;
    const modelElement = data.item;
    const { placeholder } = this;
    const viewFigure = editor.editing.mapper.toViewElement(modelElement);
    const viewWriter: ViewDowncastWriter = conversionApi.writer;
    const insertionPosition: ModelPosition = model.createPositionBefore(modelElement);
    const { unit } = CKEBulbData.page;
    const contentBlock = CKEBulbData.page.unit.contentBlocks[modelElement.getAttribute('bulb-block-id') as string];

    // Create the asset object.
    const assetType = contentBlock.blockType;
    const assetId = (assetType === 'AUDIO') ? contentBlock.audio.id : contentBlock.video.id;
    const unitId = unit.id;
    const contentBlockId = contentBlock.id;
    const assetObject = {
      assetType, assetId, unitId, contentBlockId,
    };

    // Start appear effect if needed - see https://github.com/ckeditor/ckeditor5-image/issues/191.

    // Ignore transcoding status call when it's called on undo/redo.
    if (CKEBulbData.service.undoRedo.undoRedoPlaceholderProgress) {
      editor.execute(CKEBulbData.service.undoRedo.action);
      return;
    }
    startAppearEffect(viewFigure, viewWriter);
    CKEBulbData.service.ckeStateService.handleSaveOperation.next(true);
    BulbTranscodingProgress.showPlaceholder(placeholder, viewFigure, viewWriter, toTitleCase(assetType), editor.t);
    const progressBarParam = {
      viewFigure,
      viewWriter,
      view: editor.editing.view,
      progress: 0,
      translate: editor.t,
      transcoding: true,
      instantPlay: false,
      assetType,
    };
    const progressBar = initializeProgressBar(progressBarParam);
    if (assetType === 'AUDIO') {
      const audioTranscoding = {
        assetObject,
        viewFigure,
        viewWriter,
        editor,
        progressBar,
        modelElement,
        insertionPosition,
        instantPlay: false,
        videoElement: '',
        contentBlockId,
      };
      BulbTranscodingProgress.beginTranscoding(audioTranscoding);
    } else {
      this.videoTranscoding(
        contentBlock,
        editor,
        modelElement,
        insertionPosition,
        assetObject,
        viewFigure,
        viewWriter,
        progressBar,
      );
    }
  }

  /**
   * method that handle transcoding in video.
   * @param contentBlock  content block information.
   * @param editor  editor instance.
   * @param modelElement Model element.
   * @param insertionPosition ModelPosition of asset insertion.
   * @param assetObject Contains asset information.
   * @param viewFigure Figure element.
   * @param viewWriter writer.
   * @param progressBar Contains progress bar information.
   */
  private videoTranscoding(
    contentBlock,
    editor: Editor,
    modelElement: ModelElement,
    insertionPosition: ModelPosition,
    assetObject,
    viewFigure: ViewElement,
    viewWriter: ViewDowncastWriter,
    progressBar,
  ) {
    BulbTranscodingProgress.captureSplashImage(contentBlock.video.originalAssetUrl).then((url) => {
      CKEBulbData.service.ckeStateService.capturedSplashUrl.next(url);
      editor.execute('insertVideo', contentBlock, modelElement, insertionPosition, 'instantPlay');
    }, () => {
      const videoTranscoding = {
        assetObject,
        viewFigure,
        viewWriter,
        editor,
        progressBar,
        modelElement,
        insertionPosition,
        instantPlay: false,
        videoElement: '',
        contentBlockId: contentBlock.id,
      };
      BulbTranscodingProgress.beginTranscoding(videoTranscoding);
    });

    const subscribe = CKEBulbData.service.ckeStateService.startTranscoding.subscribe((res) => {
      if (res !== '') {
        subscribe.unsubscribe();
        const transcodingProgressBar = {
          viewFigure: res.figure,
          viewWriter: res.viewWriter,
          view: editor.editing.view,
          progress: 0,
          translate: editor.t,
          transcoding: true,
          instantPlay: true,
        };
        const progressBar = initializeProgressBar(transcodingProgressBar);
        CKEBulbData.service.ckeStateService.startTranscoding.next('');
        const instantPlayTranscoding = {
          assetObject,
          viewFigure: res.figure,
          viewWriter: res.viewWriter,
          editor,
          progressBar,
          modelElement,
          insertionPosition,
          instantPlay: true,
          videoElement: res.modelElement,
          contentBlockId: contentBlock.id,
        };
        BulbTranscodingProgress.beginTranscoding(instantPlayTranscoding);
      }
    });
  }

  /**
   * Shows placeholder together with infinite progress bar on given transcoding figure.
   *
   *
   * @param placeholder placeholder element during transcoding.
   * @param viewFigure current viewFigure in the editor.
   * @param writer editor conversion writer.
   * @param assetType assetType information.
   * @param t translations service
   *
   * @private
   */
  private static showPlaceholder(
    placeholder,
    viewFigure,
    writer: ViewDowncastWriter,
    assetType: string,
    t: LocaleTranslate,
  ) {
    if (!viewFigure.hasClass('ck-image-upload-placeholder')) {
      writer.addClass('ck-image-upload-placeholder', viewFigure);
      writer.setStyle('background', 'linear-gradient(0deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),'
        + `url(${generateTranscodingBackground()});`, viewFigure);
    }

    const viewImg = viewFigure.getChild(0);

    if (viewImg.getAttribute('src') !== placeholder) {
      writer.setAttribute('src', placeholder, viewImg);
    }

    if (!getUIElement(viewFigure, 'placeholder')) {
      writer.insert(
        writer.createPositionAfter(viewImg),
        BulbTranscodingProgress.createPlaceholder(writer, assetType, t),
      );
    }
  }

  /**
   * Create placeholder element using {@link module:engine/view/uielement~UIElement}.
   *
   * @param writer
   * @param assetType
   * @param t
   * @returns {module:engine/view/containerelement~ContainerElement}
   * @private
   */
  private static createPlaceholder(writer: ViewDowncastWriter, assetType: string, t: LocaleTranslate): ViewContainerElement {
    const placeholderElement: ViewContainerElement = writer.createContainerElement('div', {
      class: `ck-upload-placeholder-loader ${assetType === 'Audio' ? 'bulb-audio-placeholder' : ''}`,
    });
    if (assetType === 'Video') {
      const videoLoad: ViewContainerElement = writer.createContainerElement(
        'div',
        { class: 'bulb-cke-instant-video-placeholder' },
      );
      const processingText = writer.createText(t('transcoding_video_message'));
      writer.insert(writer.createPositionAt(videoLoad, 0), processingText);
      writer.insert(writer.createPositionAt(placeholderElement, 'end'), videoLoad);
    } else {
      // Create the icon and file-name inside of the placeholder.
      const iconElement = writer.createUIElement('icon', { class: 'far fa-cog fa-spin bulb-transcoding-icon' });
      const processingElement: ViewContainerElement = writer.createContainerElement(
        'div',
        { class: 'bulb-transcoding-header text-ellipsis' },
      );
      const subElement: ViewContainerElement = writer.createContainerElement(
        'div',
        { class: 'bulb-transcoding-secondary-text' },
      );
      const processingText = writer.createText(t(`editor_${assetType.toLowerCase()}_processing`));
      const subText = writer.createText(t('editor_processing_message'));

      // Insert the new things into the upload block.
      writer.insert(writer.createPositionAt(processingElement, 0), processingText);
      writer.insert(writer.createPositionAt(subElement, 0), subText);
      writer.insert(writer.createPositionAt(placeholderElement, 'end'), iconElement);
      writer.insert(writer.createPositionAt(placeholderElement, 'end'), processingElement);
      writer.insert(writer.createPositionAt(placeholderElement, 'end'), subElement);

      writer.setCustomProperty('placeholder', true, placeholderElement);
    }
    return placeholderElement;
  }

  /**
   * Method for capturing url for thumnail used in instant play video.
   * @param videoUrl Url of video which was uploaded.
   */
  static captureSplashImage(videoUrl) {
    return new Promise((resolve, reject) => {
      const videoElement = document.createElement('video');
      videoElement.src = `${videoUrl}#t=0.5`;
      videoElement.crossOrigin = 'anonymous';
      videoElement.onloadedmetadata = () => {
        videoElement.width = videoElement.videoWidth;
        videoElement.height = videoElement.videoHeight;
        // If the video is at least 5 seconds long, then the frame at the 5 second mark should be used.
        if (videoElement.duration >= 5) {
          videoElement.currentTime = 5;
        }
        videoElement.onloadeddata = () => {
          const canvas = document.createElement('canvas');
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
          const imageUrl = canvas.toDataURL('image/jpg');
          resolve(imageUrl);
        };
      };
      videoElement.onerror = (event) => {
        reject(event);
      };
    });
  }

  /**
   * Method that handles transcoding of audio and video assets.
   * @param transcoding  object containing all the necessary transcoding information.
   */
  static beginTranscoding(transcoding) {
    const { assetObject } = transcoding;
    const { viewFigure } = transcoding;
    const { viewWriter } = transcoding;
    const { editor } = transcoding;
    const { progressBar } = transcoding;
    const { modelElement } = transcoding;
    const { insertionPosition } = transcoding;
    const { videoElement } = transcoding;

    const intervalCallback = setInterval(() => {
      const xmlProgressResponse = checkTranscodingProgress(assetObject);
      if (xmlProgressResponse === 'clearInterval') {
        clearInterval(intervalCallback);
      } else {
        xmlProgressResponse.then((response) => {
          updateProgressBar(editor.editing.view, progressBar, response.percentComplete);
          if (response.complete) {
            clearInterval(intervalCallback);
            clean(viewFigure, viewWriter);
            if (BulbTranscodingProgress.isVideoIdle(transcoding.contentBlockId)) {
              getUpdatedContentBlock(editor, assetObject, modelElement, insertionPosition);
              if (transcoding.instantPlay) {
                editor.model.change((writer) => {
                  writer.remove(videoElement);
                });
              }
            } else {
              editor.model.change(() => {
                refreshAsset(viewFigure, viewWriter, editor.t);
              });
              const refreshBorder = document.querySelector(
                `div[refreshId='${viewFigure.getAttribute('bulb-block-id')}']`,
              );
              const refreshBarMessage = document.querySelector(
                `div[refreshMessageId='${viewFigure.getAttribute('bulb-block-id')}']`,
              );
              refreshBorder.addEventListener(
                'click',
                refreshVideo.bind(editor, assetObject, modelElement, insertionPosition, videoElement),
              );
              refreshBarMessage.addEventListener(
                'click',
                refreshVideo.bind(editor, assetObject, modelElement, insertionPosition, videoElement),
              );
            }
          }
        });
      }
    }, 3000);
  }

  /**
   * Helper method to check whether the video is in idle mode, means user has not played the video.
   */
  static isVideoIdle(contentBlockId) {
    const jwPlayerContentBlock = BulbTranscodingProgress.getJWPlayerVideoContentBlock(contentBlockId);
    return (!jwPlayerContentBlock || !jwPlayerContentBlock.querySelector('video')
      || jwPlayerContentBlock.classList.contains('jw-state-idle')
      || jwPlayerContentBlock.classList.contains('jw-error')
    );
  }

  /**
   * Method to get JWPlayer video content block.
   */
  static getJWPlayerVideoContentBlock(contentBlockId) {
    return document.querySelector(`div[id='${contentBlockId}']`);
  }
}
